/**
 * The Onboarding V2 wizard's step tables.
 *
 * `onboardingStages` is the ordered list the Previous button walks backwards
 * through; note the four commented-out entries, which is why several live
 * `onboardingStepV2` branches are not reachable from the wizard itself.
 *
 * `onboardingSteps` drives the left-hand `<Stepper>` and the completion
 * percentage. It is deliberately shorter than `onboardingStages`: the
 * generation/deal-creation stages have no stepper entry.
 */
const onboardingStages = [
  'company_info',
  'value_prop',
  'value_prop_generated',
  // 'deal_risk_selection',
  'crm_integration',
  'deal_stages',
  'tools_integrations',
  'selling_style',
  'report_schedule',
  // 'discovery_questions',
  // 'discovery_questions_reordering',
  'deal_creation',
];

const onboardingSteps = [
  {
    key: 'company_info',
    label: 'Your Company',
    description: 'Just some essentials to set up and personalize your account.',
    weight: 0,
  },
  {
    key: 'value_prop',
    label: 'Your Current Value Proposition',
    description:
      'Let&apos;s prepare you a value proposition designed for differentiated buyer messaging, deep insights and tailored recommendations.',
    weight: 5,
  },
  {
    key: 'value_prop_generated',
    label: 'Generated Value Proposition',
    description:
      'Here&apos;s your shiny new value prop! Please review it, make edits if needed and bless it &#128578; before proceeding.',
    weight: 30,
  },
  {
    key: 'crm_integration',
    label: 'CRM Integration (recommended)',
    description:
      'Get the best of both worlds -- power of deep AI insights on the data without the pain of manual CRM updates.',
    weight: 10,
  },
  {
    key: 'deal_stages',
    label: 'Your Sales Process',
    description:
      'Tell us your sales pipeline stages so we can recommend the right step for each deal tailored for its stage.',
    weight: 20,
  },
  {
    key: 'tools_integrations',
    label: 'Comms Integration (recommended)',
    description:
      'Never lift a finger to bring in buyer data. Don&apos;t miss anything, make sense of everything &#128161;.',
    weight: 5,
  },
  {
    key: 'selling_style',
    label: 'Your Selling Style',
    description:
      'Pick your preferred selling approach and we&apos;ll customize our recommendations and buyer messaging to match your style.',
    weight: 20,
  },
  {
    key: 'report_schedule',
    label: 'Your Weekly Report Schedule',
    description:
      'Choose the day and time your whole team gets its weekly deal summary, in your organization&apos;s timezone.',
    weight: 10,
  },
];

/**
 * The CRM and comms-tool cards on the `crm_integration` and
 * `tools_integrations` steps. Only onboarding renders these; `/integrations`
 * builds its own list.
 */
const crms = [
  { crm: 'hubspot', name: 'Hubspot', icon: '/hubspot.svg', iconWidth: '120px' },
  {
    crm: 'pipedrive',
    name: 'Pipedrive',
    icon: '/pipedrive-logo.png',
    iconWidth: '140px',
  },
  { crm: 'zohocrm', name: 'Zoho', icon: '/zoho-crm.svg', iconWidth: '100px' },
];

const tools = [
  {
    tool: 'gmail',
    name: 'Gmail',
    icon: '/dh-icons/gmail.svg',
    iconWidth: '100px',
  },
  {
    tool: 'google-calendar',
    name: 'Google Calendar',
    icon: '/google_calendar.png',
    iconWidth: '140px',
  },
  {
    tool: 'slack',
    name: 'Slack',
    icon: '/dh-icons/slack.png',
    iconWidth: '100px',
  },
  {
    tool: 'fathom',
    name: 'Fathom',
    icon: '/dh-icons/fathom.svg',
    iconWidth: '100px',
  },
  {
    tool: 'zoom',
    name: 'Zoom',
    icon: '/zoom_logo.svg',
    iconWidth: '70px',
  },
  {
    tool: 'aircall',
    name: 'Aircall',
    icon: '/dh-icons/aircall.png',
    iconWidth: '100px',
  },
];

/**
 * The two discovery-tree stages, and the two discovery-question stages, were
 * removed from the wizard: none had a reachable entry point and their Next
 * buttons were permanently disabled or led to a step with no component. The
 * backend can still return any of them, so each maps to what its Next used to
 * do rather than leaving the wizard parked on a stage it can no longer render.
 *
 * Lifted out of `OnboardingDialogV2`'s component body so that
 * `useOnboardingProgress` can resolve a stage *before* deciding whether to
 * open the dialog at all -- a retired stage that redirects to
 * `onboarding_completed` must not open it.
 */
const RETIRED_STAGE_REDIRECTS = {
  signup_completed: 'company_info',
  discovery_tree_generation_in_progress: 'crm_integration',
  discovery_tree_generation_in_progress_after: 'onboarding_completed',
  // Retired with bug 28: `discovery_questions` advanced to
  // `discovery_questions_reordering`, which has no step component and no
  // footer, so it was a dead end. Both are commented out of
  // `onboardingStages`; the backend can still return either.
  discovery_questions: 'onboarding_completed',
  discovery_questions_reordering: 'onboarding_completed',
};

/**
 * Maps a stage as the backend reports it to the stage the wizard renders.
 *
 * @param {string} stage `orgConfigs.onboardingStage`
 * @returns {string} the stage to render, or `stage` unchanged
 */
const resolveOnboardingStage = (stage) =>
  RETIRED_STAGE_REDIRECTS[stage] || stage;

/**
 * Whether a reported stage proves the wizard still has work to do.
 *
 * Deliberately requires *proof*: an empty or missing stage returns `false`.
 * The wizard's body is `onboardingStage && onboardingStepV2 !== '' ? wizard
 * : spinner`, so opening on an empty stage could only ever paint a spinner
 * with nothing behind it.
 *
 * @param {string} stage `orgConfigs.onboardingStage`
 * @returns {boolean}
 */
const isOnboardingIncomplete = (stage) => {
  const effectiveStage = resolveOnboardingStage(stage);
  return Boolean(effectiveStage) && effectiveStage !== 'onboarding_completed';
};

export {
  onboardingStages,
  onboardingSteps,
  crms,
  tools,
  RETIRED_STAGE_REDIRECTS,
  resolveOnboardingStage,
  isOnboardingIncomplete,
};
